import React, { useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ArrowRight, Smartphone, Sparkles, Menu, X } from 'lucide-react';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import ContactPage from './ContactPage';
import BusinessConsultationPage from './BusinessConsultationPage';
import EbookPage from './EbookPage';
import AppsPage from './AppsPage';
import AppDetailPage from './AppDetailPage';
import AppCard from './components/AppCard';
import { apps, developerPageUrl } from './data/apps';
import { useGeoLocation } from './hooks/useGeoLocation';

const HomePage = () => {
  const productsRef = useRef(null);
  const { downloadUrl } = useGeoLocation();

  const scrollToProducts = () => {
    productsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Kabett - Building Useful Products for Everyday Life</title>
        <meta name="description" content="Kabett creates smart, simple solutions for everyday challenges. Discover our innovative products designed to make your life easier." />
        <meta name="keywords" content="Kabett, productivity tools, health apps, wellness applications, everyday solutions" />
        <meta property="og:title" content="Kabett - Innovative Solutions for Daily Life" />
        <meta property="og:description" content="Discover Kabett's suite of products designed to enhance your daily life and productivity." />
        <meta property="og:image" content="https://kabett.com/kabett-screen.webp" />
        <meta property="og:url" content="https://kabett.com/" />
        {/* Self-referencing canonical: collapses tracking-param variants like ?utm_source / ?from (e.g. AppAgg.com referrals) onto the clean homepage */}
        <link rel="canonical" href="https://kabett.com/" />
      </Helmet>

      {/* Announcement Banner */}
      <div className="bg-[#BAB37A] text-white py-2 mb-12 -mt-4 rounded-lg">
        <div className="flex items-center justify-between px-6">
          <div className="flex items-center space-x-3">
            <Sparkles size={20} />
            <p className="text-sm md:text-base">
              <span className="font-semibold">New Release:</span> Learn Kannada Akshara — the fun Kannada alphabet app for kids
            </p>
          </div>
          <a
            href="/learn-kannada-alphabet/"
            className="flex items-center space-x-2 bg-white text-[#BAB37A] px-4 py-1 rounded-full text-sm font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            <span>Explore</span>
            <ArrowRight size={16} />
          </a>
        </div>
      </div>

      <section className="flex flex-col md:flex-row items-start justify-between mb-24">
        <div className="md:w-1/2 mb-12 md:mb-0 md:pr-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-8 leading-tight">
            Building Useful Products for Everyday Life
          </h1>
          <p className="text-xl mb-8 max-w-2xl text-gray-600">
            Ever wished for tools that just make life easier? That's what we do at Kabett. We're all about creating smart, simple solutions for everyday challenges — from helping kids fall in love with the Kannada alphabet to bringing a moment of calm into your busy day. Ready to discover how we can make your day a little bit better?
          </p>
          <motion.button
            className="bg-transparent text-[#BAB37A] px-6 py-3 rounded-full font-semibold flex items-center space-x-2 hover:bg-[#BAB37A] hover:text-white transition-colors border border-[#BAB37A]"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToProducts}
          >
            <span>Explore Our Products</span>
            <ArrowRight size={20} />
          </motion.button>
        </div>
        <div className="md:w-1/2">
          <div className="w-full h-0 pb-[75%] relative rounded-lg overflow-hidden">
            <img 
              src="/kabett-header.webp" 
              alt="Kabett products enhancing everyday life" 
              className="absolute top-0 left-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Featured Apps Section */}
      <section ref={productsRef} className="mb-16">
        <h2 className="text-3xl font-serif font-bold mb-8">Featured Apps</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {/* Learn Kannada Akshara */}
          <motion.div
            className="bg-rose-50 rounded-lg p-8 flex flex-col"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center gap-4 mb-5">
              <img
                src="/images/apps/learn-kannada-akshara.webp"
                alt="Learn Kannada Akshara app icon"
                className="w-16 h-16 rounded-2xl shadow-lg object-cover"
              />
              <div>
                <h3 className="text-2xl font-serif font-bold">Learn Kannada Akshara</h3>
                <p className="text-sm text-gray-500">Kannada alphabet for kids · Ages 3+ · Free</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6 flex-grow">
              Colorful flashcards, native audio pronunciation, and guided finger-tracing for all 49
              Kannada letters — the fun way for kids to learn to read and write Kannada.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <a
                href="/learn-kannada-alphabet/"
                className="bg-[#F2547D] text-white px-5 py-2.5 rounded-full text-sm font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity"
              >
                <span>Explore the app</span>
                <ArrowRight size={16} />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.kabett.kannadakidslearning"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 text-sm font-semibold"
              >
                Google Play →
              </a>
            </div>
          </motion.div>

          {/* Breath Break Pranayama */}
          <motion.div
            className="bg-green-50 rounded-lg p-8 flex flex-col"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center gap-4 mb-5">
              <img
                src="/images/apps/breath-break-pranayama.webp"
                alt="Breath Break Pranayama app icon"
                className="w-16 h-16 rounded-2xl shadow-lg object-cover"
              />
              <div>
                <h3 className="text-2xl font-serif font-bold">Breath Break: Pranayama</h3>
                <p className="text-sm text-gray-500">Guided breathing · Free · No ads</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6 flex-grow">
              Guided Anuloma Viloma — alternate nostril breathing — with calm voice instructions,
              custom session timing, and daily streaks. No ads, no subscriptions, works offline.
            </p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <a
                href="/anuloma-viloma/"
                className="bg-[#2E5C50] text-white px-5 py-2.5 rounded-full text-sm font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity"
              >
                <span>Explore the app</span>
                <ArrowRight size={16} />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=net.breathbreak.pranayama"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 text-sm font-semibold"
              >
                Google Play →
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Apps Section */}
      <section className="mb-16">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-[#BAB37A] p-2 rounded-lg">
              <Smartphone className="text-white" size={20} />
            </div>
            <h2 className="text-3xl font-serif font-bold">Our Apps</h2>
          </div>
          <Link
            to="/apps"
            className="text-[#BAB37A] hover:text-[#9A9463] font-semibold flex items-center gap-2"
          >
            <span>View All</span>
            <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {apps.map((app) => (
            <AppCard key={app.id} app={app} compact={true} />
          ))}
        </div>
        <div className="text-center mt-6">
          <a
            href={developerPageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            View all on Google Play →
          </a>
        </div>
      </section>

      {/* Products & Services Section */}
      <section className="mb-24">
        <h2 className="text-3xl font-serif font-bold mb-8">Products & Services</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-blue-50 p-8 rounded-lg">
            <div className="flex justify-center mb-6">
              <img src="/mumma-care.png" alt="MummaCare Logo" className="w-24 h-24 rounded-full object-cover" />
            </div>
            <h3 className="text-xl font-serif font-bold mb-3 text-center">MummaCare</h3>
            <p className="mb-4 text-center text-gray-600 text-sm">Empowering mothers with comprehensive care and support throughout their journey.</p>
            <div className="text-center">
              <a href="https://mummacare.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">Visit MummaCare</a>
            </div>
          </div>
          <div className="bg-yellow-50 p-8 rounded-lg">
            <div className="flex justify-center mb-6">
              <img src="/kabett-business-consultancy.webp" alt="Business Consultation Logo" className="w-24 h-24 rounded-full object-cover" />
            </div>
            <h3 className="text-xl font-serif font-bold mb-3 text-center">Business Consultation</h3>
            <p className="mb-4 text-center text-gray-600 text-sm">Expert guidance to take your business to the next level with our Small Entrepreneurship Solutions.</p>
            <div className="text-center">
              <Link to="/consultation" className="text-blue-600 hover:underline text-sm">Learn More</Link>
            </div>
          </div>
          <div className="bg-green-50 p-8 rounded-lg">
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 rounded-full overflow-hidden">
                <img
                  src="images/ebook/ebook-cover-family-business-playbook.webp"
                  alt="Family Business Ebook"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center top' }}
                />
              </div>
            </div>
            <h3 className="text-xl font-serif font-bold mb-3 text-center">Family Business Guide</h3>
            <p className="mb-4 text-center text-gray-600 text-sm">Master the art of running a successful family business with our comprehensive guide.</p>
            <div className="text-center">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                Get the Ebook
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const App = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: '/apps', label: 'Apps' },
    { to: '/consultation', label: 'Consultation' },
    { to: '/ebook', label: 'Ebook' },
    { to: '/privacy-policy', label: 'Privacy Policy' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <Router>
      <div className="min-h-screen bg-white font-sans flex flex-col">
        <Helmet>
          <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Merriweather:wght@400;700&display=swap" rel="stylesheet" />
        </Helmet>
        <header className="container mx-auto px-8 py-6">
          <nav className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/kabett401.webp" alt="Kabett Logo" className="w-12 h-12" />
              <div className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '1px' }}>Kabett</div>
            </Link>

            {/* Hamburger button - mobile only */}
            <button
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Desktop nav - hidden on mobile */}
            <ul className="hidden md:flex space-x-6">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-600 hover:text-gray-900 bg-transparent border-none cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Mobile menu - shown when open */}
          {mobileMenuOpen && (
            <ul className="md:hidden flex flex-col space-y-4 py-4 border-t mt-4">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-gray-600 hover:text-gray-900 block py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </header>

        <main className="container mx-auto px-8 py-4 flex-grow">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/apps" element={<AppsPage />} />
            <Route path="/apps/:slug" element={<AppDetailPage />} />
            <Route path="/consultation" element={<BusinessConsultationPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/ebook" element={<EbookPage />} />
          </Routes>
        </main>

        <footer className="bg-[#F3F1E4] py-8 mt-12">
          <div className="container mx-auto px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-[#BAB37A] font-semibold mb-4 md:mb-0">&copy; 2025 Kabett. All rights reserved.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/apps" className="text-[#BAB37A] hover:text-[#9A9463]">Apps</Link>
                <a href="/learn-kannada-alphabet/" className="text-[#BAB37A] hover:text-[#9A9463]">Kannada Alphabet</a>
                <a href="/anuloma-viloma/" className="text-[#BAB37A] hover:text-[#9A9463]">Breathing App</a>
                <Link to="/consultation" className="text-[#BAB37A] hover:text-[#9A9463]">Consultation</Link>
                <Link to="/ebook" className="text-[#BAB37A] hover:text-[#9A9463]">Ebook</Link>
                <Link to="/privacy-policy" className="text-[#BAB37A] hover:text-[#9A9463]">Privacy Policy</Link>
                <Link to="/contact" className="text-[#BAB37A] hover:text-[#9A9463]">Contact</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
};

export default App;